// @flow

import { useSelector } from "react-redux";
import React, { useMemo } from "react";
import * as R from "ramda";
import useClientDimensions from "src/hooks/useClientDimensions";
import type { RoomId } from "src/types";

import { Value, DueStatus } from "./styles";
import statusConstants, {
  ACTIVE,
  COMPLETED,
  CANCELED,
} from "src/constants/status";
import {
  getChatroomType,
  isStatusDisabled,
  getChatroomManualArchived,
} from "src/reducers";
import Icons from "src/icons";
import Archived from "./Archived";

type Props = {
  id: number,
  isOverDue?: boolean,
  hideArchive?: boolean,
  location?: string,
  active?: boolean,
  roomId?: RoomId,
  customCss?: string,
};

const Standard = ({
  id,
  isOverDue,
  hideArchive,
  location,
  active,
  roomId,
  customCss,
}: Props) => {
  const { vw } = useClientDimensions();
  const type = useSelector(({ app }) =>
    roomId ? getChatroomType(app, `${roomId}`) : null,
  );
  const disabled = useSelector(({ app }) =>
    roomId ? isStatusDisabled(app, roomId) : null,
  );
  const manuallyArchived = useSelector(({ app }) =>
    roomId ? getChatroomManualArchived(app, roomId) : null,
  );

  const status = useMemo(() => {
    if (type === "workflow" && disabled && R.includes(id, [-1, -2, 2])) {
      return {
        ...(statusConstants[`${id}`] || {}),
        text: ACTIVE,
        color: statusConstants[-1].color,
        textColor: statusConstants[-1].textColor,
      };
    }
    if (type === "group" && id === -1) {
      return { ...(statusConstants[`${id}`] || {}), text: ACTIVE };
    }
    return statusConstants[`${id}`] || {};
  }, [id, type, disabled]);

  if (isOverDue && !disabled && !manuallyArchived) {
    if (id === COMPLETED || id === CANCELED) {
      return (
        <Value
          color={status.color}
          textColor={status.textColor}
          location={location}
          vw={vw}
          customCss={customCss}
        >
          <DueStatus>{status.text}</DueStatus>
        </Value>
      );
    }
    return (
      <Value
        color={status.color}
        textColor={status.textColor}
        location={location}
        overdue={type !== "direct" && type !== "group" && isOverDue}
        active={active}
        customCss={customCss}
      >
        <DueStatus>
          <div>{status.text}</div>
        </DueStatus>
        <Icons type="alarmClock" />
      </Value>
    );
  }
  return (
    <Value
      color={status.color}
      textColor={status.textColor}
      location={location}
      vw={vw}
      customCss={customCss}
    >
      {status.text}
      {manuallyArchived && !hideArchive ? <Archived /> : null}
    </Value>
  );
};

export default Standard;
